// /pages/RejectSummaryPage.js

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DataGrid, {
  Column, FilterRow, HeaderFilter, SearchPanel, Sorting, Export, Paging
} from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import notify from 'devextreme/ui/notify';
import 'devextreme/dist/css/dx.material.blue.light.css'; 
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config';

const REJECT_SUMMARY_API_URL = `${API_URL}/api/oracle/reject-summary`;
const CUST_API_URL = `${API_URL}/api/local/common/custs`;
const DEVICE_API_URL = `${API_URL}/api/local/common/devices`;

export default function RejectSummaryPage({ user }) {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [data, setData] = useState([]);
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  
  const [selectedFactory] = useState('FT01');
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState('ALL');
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}${month}${day}`;
  };

  useEffect(() => {
    if (!selectedFactory) return;
    axios.get(CUST_API_URL, { params: { factory: selectedFactory } })
      .then(res => setCustList(res.data.data || []))
      .catch(err => console.error("CUST 목록 조회 실패:", err));
  }, [selectedFactory]);

  useEffect(() => {
    if (!selectedFactory || !selectedCust) {
      setDeviceList([]);
      setSelectedDevice('ALL');
      return;
    }
    axios.get(DEVICE_API_URL, { params: { factory: selectedFactory, custCd: selectedCust } })
      .then(res => {
        const fetchedDevices = res.data.data || [];
        setDeviceList([{ CODE_CD: 'ALL' }, ...fetchedDevices]);
        setSelectedDevice('ALL');
      })
      .catch(err => console.error("DEVICE 목록 조회 실패:", err));
  }, [selectedFactory, selectedCust]);

  useEffect(() => {
    if (user?.role === "CUST" && custList.length > 0) {
      const found = custList.find(c => c.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
      if (found) setSelectedCust(found.CODE_CD);
    }
  }, [user, custList]);

  const fetchData = useCallback(() => {
    if (!selectedFactory || !selectedCust) {
      notify("Factory와 CUST는 필수 선택 항목입니다.", "warning", 2000);
      return;
    }
    setLoading(true);
    setError("");
    setData([]);

    const params = {
      factory: selectedFactory,
      custCd: selectedCust,
      device: selectedDevice,
      dateFrom: formatDate(dateFrom),
      dateTo: formatDate(dateTo),
    };
    
    axios.get(REJECT_SUMMARY_API_URL, { params })
      .then(res => {
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setData(res.data.data.map((row, idx) => ({ ...row, id: idx })));
      })
      .catch(e => {
        const errorMessage = e.response?.data?.retMsg || e.message;
        setError(`조회 오류: ${errorMessage}`);
        notify(errorMessage, 'error', 3000);
        setData([]);
      })
      .finally(() => setLoading(false));
  }, [selectedFactory, selectedCust, selectedDevice, dateFrom, dateTo]);

  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Reject_Summary_Report");
    exportDataGrid({
        component: e.component,
        worksheet,
        autoFilterEnabled: true
    }).then(() => {
        workbook.xlsx.writeBuffer().then(buffer => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Reject_Summary_Report.xlsx");
        });
    });
    e.cancel = true;
  };

  // 1. 행 배경색 및 스타일 지정 (ROLLUP IDX 기준)
  const onRowPrepared = (e) => {
    if (e.rowType === 'data') {
      const idx = e.data.IDX;
      if (idx === 63) { 
        // Device 요약 행 (초록색)
        e.rowElement.style.backgroundColor = '#b2ff66'; 
        e.rowElement.style.fontWeight = 'bold';
      } else if (idx === 127) { 
        // Customer 총합 행 (주황색)
        e.rowElement.style.backgroundColor = '#ffcc00'; 
        e.rowElement.style.fontWeight = 'bold';
      }
    }
  };

  // 2. Customer 컬럼 커스텀 렌더링 (방어 코드 포함)
  const customerCellRender = (cellData) => {
    // 필터Row 등 데이터가 없는 영역 처리 시 에러 방지
    if (!cellData || !cellData.data) return null; 

    const { data } = cellData;
    if (data.IDX === 63) {
      return <span>( SUM : {data.MAT_ID} )</span>;
    } else if (data.IDX === 127) {
      return <span>( SUM : {data.CUSTOMER_ID} )</span>;
    }
    return <span>{data.CUSTOMER_ID}</span>;
  };

  // 3. 합계 행(IDX=63, 127)에서 불필요한 데이터 숨김 처리
  const hideSummaryCellRender = (cellData) => {
    // 필터Row 등 데이터가 없는 영역 처리 시 에러 방지
    if (!cellData || !cellData.data) return null; 

    // 소계, 총합계 행일 경우 빈칸으로 표시
    if (cellData.data.IDX === 63 || cellData.data.IDX === 127) {
      return <span></span>; 
    }
    
    // 일반 데이터일 경우 포맷(천 단위 콤마 등)이 적용된 텍스트 출력
    return <span>{cellData.text}</span>; 
  };

  return (
    <div style={{ padding: 20, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>Reject Summary Report</h3>
        <div style={{ display: "flex", gap: 15, flexWrap: "wrap", alignItems: 'center' }}>
          <DateBox value={dateFrom} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateFrom(e.value)} label="FROM" width={180} />
          <DateBox value={dateTo} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateTo(e.value)} label="TO" width={180} />
          <SelectBox items={['FT01']} value={selectedFactory} label="FACTORY" width={150} />
          <SelectBox dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_NM" onValueChanged={e => setSelectedCust(e.value)} disabled={user?.role === "CUST"} searchEnabled showClearButton={user?.role !== "CUST"} placeholder="Select CUST" label="CUST" width={220} />
          <SelectBox dataSource={deviceList} value={selectedDevice} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedDevice(e.value)} searchEnabled label="DEVICE" width={220} />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "#d9534f", fontWeight: 'bold', marginTop: '10px' }}>{error}</p>}
      </div>
      
      <div style={{ flex: 1, position: 'relative' }}>
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
          </div>
        )}
        <DataGrid
          dataSource={data}
          keyExpr="id"
          onExporting={onExporting}
          onRowPrepared={onRowPrepared}
          height="100%"
          showBorders={true}
          allowColumnResizing={true}
          columnAutoWidth={true}
          rowAlternationEnabled={true}
        >
          <Export enabled={true} />
          <Paging defaultPageSize={50} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} width={240} />
          <Sorting mode="multiple" />
    
          {/* 컬럼 정의 영역 */}
          <Column dataField="CUSTOMER_ID" caption="Customer" width={200} cellRender={customerCellRender} />
          <Column dataField="MAT_ID" caption="Device" width={180} cellRender={hideSummaryCellRender} />
          <Column dataField="LOT_ID" caption="Lot ID" width={150} cellRender={hideSummaryCellRender} />
          <Column dataField="WW" caption="W.W" width={80} cellRender={hideSummaryCellRender} />
          <Column dataField="LEAD" caption="LEAD" width={100} cellRender={hideSummaryCellRender} />
          <Column dataField="PKG_TYPE" caption="PKG Type" width={120} cellRender={hideSummaryCellRender} />
          <Column dataField="OPER" caption="Oper" width={80} cellRender={hideSummaryCellRender} />
          <Column dataField="OUT_TIME" caption="Out Time" width={160} cellRender={hideSummaryCellRender} />
          
          <Column dataField="INPUT_QTY" caption="Input Qty" dataType="number" format="#,##0" alignment="right" width={100} cellRender={hideSummaryCellRender} />
          <Column dataField="OUT_QTY" caption="Out Qty" dataType="number" format="#,##0" alignment="right" width={100} cellRender={hideSummaryCellRender} />
          <Column dataField="REJECT_QTY" caption="Reject Qty" dataType="number" format="#,##0" alignment="right" width={100} />
          <Column dataField="YIELD" caption="Yield" alignment="right" width={100} cellRender={hideSummaryCellRender} />
          
        </DataGrid>
      </div>
    </div>
  );
}